import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Group, Text } from '@wapharma/design-system';
import {
  attachCrashScreenshotToTicket,
  buildReportIssueContext,
  getLastTraceId,
  readReproBreadcrumbs,
  setLastSentryEventId,
  unwrapData,
} from '@wapharma/shared';
import { API_BASE } from '../lib/apiBase';
import { getToken } from '../auth';
import { AdminStatusPage } from './AdminStatusPage';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  sentryEventId: string | null;
  lastErrorMessage: string | null;
};

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, sentryEventId: null, lastErrorMessage: null };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const eventId = Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    if (eventId) {
      setLastSentryEventId(eventId);
      this.setState({ sentryEventId: eventId });
    }
    this.setState({ lastErrorMessage: `${error.name}: ${error.message}` });

    // Auto-ticket (best effort). Dedupe by Sentry event id in this tab.
    try {
      const key = eventId ? `wapharma_auto_ticket:${eventId}` : null;
      if (key && sessionStorage.getItem(key) === '1') return;
      if (key) sessionStorage.setItem(key, '1');

      const traceId = getLastTraceId();
      const token = getToken();
      const ctx = buildReportIssueContext({
        surface: 'admin',
        route: window.location.pathname + window.location.search,
        componentStack: errorInfo.componentStack ?? undefined,
      });
      const repro = readReproBreadcrumbs();

      void fetch(`${API_BASE}/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: 'BUG',
          subject: `Crash admin ${window.location.pathname}`,
          description: `${error.name}: ${error.message}`,
          priority: 'HIGH',
          context: { ...ctx, repro, traceId, sentryEventId: eventId },
        }),
      })
        .then(async (res) => {
          if (!res.ok) return;
          const body = (await res.json()) as unknown;
          const ticket = unwrapData<{ id: string }>(body);
          if (!ticket?.id) return;
          await attachCrashScreenshotToTicket({
            apiBase: API_BASE,
            ticketId: ticket.id,
            sentryEventId: eventId,
            token,
          });
        })
        .catch(() => {
          // ignore
        });
    } catch {
      // ignore
    }
  }

  private handleRetry = () => window.location.reload();

  private handleGoHome = () => {
    window.location.assign('/');
  };

  private handleCopyTraceId = async () => {
    const v = getLastTraceId()?.trim();
    if (!v) return;
    try {
      await window.navigator.clipboard.writeText(v);
    } catch {
      try {
        const el = document.createElement('textarea');
        el.value = v;
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      } catch {
        // ignore
      }
    }
  };

  render() {
    if (this.state.hasError) {
      const msg = String(this.state.lastErrorMessage ?? '');
      const isRecoverableChunk =
        /chunkloaderror/i.test(msg) ||
        /loading chunk/i.test(msg) ||
        /failed to fetch dynamically imported module/i.test(msg);
      const traceId = getLastTraceId();
      return (
        <AdminStatusPage
          mode="crash"
          variant="error"
          title="Une erreur est survenue"
          description={
            traceId ? (
              <Text size="sm" c="muted">
                Code erreur : <code>{traceId}</code>
              </Text>
            ) : undefined
          }
        >
          <Group justify="center" gap="sm" wrap="wrap">
            <Button type="button" variant="primary" onClick={this.handleGoHome}>
              Retour à l&apos;accueil
            </Button>
            {isRecoverableChunk ? (
              <Button type="button" variant="ghost" onClick={this.handleRetry}>
                Réessayer
              </Button>
            ) : null}
            {traceId?.trim() ? (
              <Button type="button" variant="ghost" onClick={() => void this.handleCopyTraceId()}>
                Copier code erreur
              </Button>
            ) : null}
          </Group>
        </AdminStatusPage>
      );
    }
    return this.props.children;
  }
}
